// src/offline/offlineMeta.ts
import { db } from "../db";

const STORAGE_ESTIMATE_EVENT = "offlineStorageEstimate";

export async function getDownloadedPrefixes(): Promise<string[]> {
  const meta = await db.meta.get("offlinePrefixes");
  return meta?.value || [];
}

export async function addDownloadedPrefix(prefix: string) {
  const current = await getDownloadedPrefixes();
  const next = Array.from(new Set([...current, prefix]));
  await db.meta.put({ key: "offlinePrefixes", value: next });
}

export async function removeDownloadedPrefix(prefix: string) {
  const current = await getDownloadedPrefixes();
  const next = current.filter(p => p !== prefix);
  await db.meta.put({ key: "offlinePrefixes", value: next });
  if (next.length === 0) {
    await clearGlobalSyncTimestamp();
  }
}

export async function getPrefixRefreshTimes(): Promise<Record<string, string>> {
  const meta = await db.meta.get("offlineRefresh");
  return meta?.value || {};
}

export async function setPrefixRefreshTime(prefix: string, ts: string) {
  const current = await getPrefixRefreshTimes();
  await db.meta.put({
    key: "offlineRefresh",
    value: {
      ...current,
      [prefix]: ts,
    },
  });
}

export async function getGlobalSyncTimestamp(): Promise<string | null> {
  const meta = await db.meta.get("offlineGlobalSync");
  return meta?.value ?? null;
}

export async function setGlobalSyncTimestamp(ts: string) {
  await db.meta.put({ key: "offlineGlobalSync", value: ts });
}

export async function clearGlobalSyncTimestamp() {
  await db.meta.delete("offlineGlobalSync");
}

export interface StorageEstimate {
  usageBytes: number;
  quotaBytes: number | null;
  updatedAt: string;
}

function broadcastStorageEstimate(estimate: StorageEstimate | null) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(STORAGE_ESTIMATE_EVENT, { detail: estimate }));
}

export async function getStorageEstimate(): Promise<StorageEstimate | null> {
  const meta = await db.meta.get("offlineStorageEstimate");
  return meta?.value ?? null;
}

export async function saveStorageEstimate(estimate: StorageEstimate) {
  await db.meta.put({ key: "offlineStorageEstimate", value: estimate });
  broadcastStorageEstimate(estimate);
}

export async function clearStorageEstimate() {
  await db.meta.delete("offlineStorageEstimate");
  broadcastStorageEstimate(null);
}

/**
 * Refreshes an approximate storage estimate (all offline data).
 * Uses browser storage API; falls back to null if unsupported.
 */
export async function refreshStorageEstimate(): Promise<StorageEstimate | null> {
  const estimator = (typeof navigator !== "undefined" && navigator.storage?.estimate) || null;
  if (!estimator) return null;

  const { usage = 0, quota = null } = await navigator.storage.estimate();
  const estimate: StorageEstimate = {
    usageBytes: usage ?? 0,
    quotaBytes: quota ?? null,
    updatedAt: new Date().toISOString(),
  };

  await saveStorageEstimate(estimate);
  return estimate;
}

export function subscribeToStorageEstimate(callback: (estimate: StorageEstimate | null) => void) {
  if (typeof window === "undefined") return () => {};
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<StorageEstimate | null>).detail ?? null;
    callback(detail);
  };
  window.addEventListener(STORAGE_ESTIMATE_EVENT, handler);
  return () => window.removeEventListener(STORAGE_ESTIMATE_EVENT, handler);
}

// Download stats (addresses downloaded per prefix)
export interface DownloadStats {
  addressesDownloaded: number;
  updatedAt: string;
}

export async function getDownloadStats(prefix: string): Promise<DownloadStats | null> {
  const meta = await db.meta.get(`offlineDownloadStats_${prefix}`);
  return meta?.value ?? null;
}

export async function setDownloadStats(prefix: string, stats: DownloadStats) {
  await db.meta.put({
    key: `offlineDownloadStats_${prefix}`,
    value: stats,
  });
}

export async function clearDownloadStats(prefix: string) {
  await db.meta.delete(`offlineDownloadStats_${prefix}`);
}
